import { format } from 'date-fns';

export type StoredSchedulerBooking = {
  startTime: string;
  endTime: string;
  inviteeName?: string;
  inviteeEmail?: string;
  externalId?: string;
  externalUrl?: string | null;
  meetUrl?: string | null;
};

export function parseStoredSchedulerBooking(
  raw: string | null | undefined,
): StoredSchedulerBooking | null {
  if (typeof raw !== 'string' || raw.trim().length === 0) return null;
  if (!raw.trim().startsWith('{')) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredSchedulerBooking>;
    if (!parsed.startTime || !parsed.endTime) return null;
    return {
      startTime: parsed.startTime,
      endTime: parsed.endTime,
      inviteeName: parsed.inviteeName ?? '',
      inviteeEmail: parsed.inviteeEmail ?? '',
      externalId: parsed.externalId,
      externalUrl: parsed.externalUrl ?? null,
      meetUrl: parsed.meetUrl ?? null,
    };
  } catch {
    return null;
  }
}

function isIsoDateString(raw: string): boolean {
  const trimmed = raw.trim();
  return /^\d{4}-\d{2}-\d{2}/.test(trimmed) || trimmed.includes('T');
}

export function formatStoredDateResponse(raw: string | null | undefined): string | null {
  if (!raw?.trim()) return null;
  if (!isIsoDateString(raw)) return raw;

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return format(date, 'd MMMM yyyy');
}

export type FormatSchedulerResponseOptions = {
  includeInvitee?: boolean;
  includeLinks?: boolean;
};

export function formatStoredSchedulerResponse(
  raw: string | null | undefined,
  options: FormatSchedulerResponseOptions = {},
): string | null {
  const booking = parseStoredSchedulerBooking(raw);
  if (!booking) return null;

  const start = new Date(booking.startTime);
  const end = new Date(booking.endTime);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return raw ?? null;

  const includeInvitee = options.includeInvitee !== false;
  const includeLinks = options.includeLinks === true;

  const parts = [
    format(start, 'EEE, d MMM yyyy'),
    `${format(start, 'h:mm a')} – ${format(end, 'h:mm a')}`,
  ];

  if (includeInvitee) {
    const name = booking.inviteeName?.trim();
    const email = booking.inviteeEmail?.trim();
    if (name && email) {
      parts.push(`${name} (${email})`);
    } else if (name) {
      parts.push(name);
    } else if (email) {
      parts.push(email);
    }
  }

  if (includeLinks) {
    if (booking.meetUrl) parts.push(booking.meetUrl);
    if (booking.externalUrl) parts.push(booking.externalUrl);
  }

  return parts.join(' · ');
}

export type FormatJourneyItemResponseOptions = FormatSchedulerResponseOptions;

export function formatJourneyItemResponseContent(
  content: string | null | undefined,
  type: string | undefined,
  options: FormatJourneyItemResponseOptions = {},
): string {
  if (!content?.trim()) return '';

  if (type === 'DATE') {
    return formatStoredDateResponse(content) ?? content;
  }

  if (type === 'SCHEDULER') {
    return formatStoredSchedulerResponse(content, options) ?? content;
  }

  return content;
}
